import React, { useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { DEFINITION_TOOLTIP_CONFIG_BY_ID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import {
	TOOLTIP_CONTENT_CLASSNAME,
	TOOLTIP_TRIGGER_DECORATION_CLASSNAME,
} from 'src/components/primitives/Tooltip/consts';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { capStringFractionDigits } from '@velocity-exchange/common/format';
import { QUOTE_SPOT_MARKET_INDEX } from '@velocity-exchange/sdk';
import { useBalanceRowData } from 'src/hooks/useBalanceRowData';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { cm } from 'src/utils/classNames';

interface Props {
	currentPositionBaseSizeString: string;
	freeCollateral: number;
	formattedMarketSymbol: string;
	reduceOnly: boolean;
	sizeDigits: number;
	marginMode: 'cross' | 'isolated';
}

const ISOLATED_AVAILABLE_TOOLTIP_STORAGE_KEY =
	'isolated-available-tooltip-status';

const MaxSizeLabel = React.memo(function MaxSizeLabel({
	currentPositionBaseSizeString,
	freeCollateral,
	formattedMarketSymbol,
	reduceOnly,
	sizeDigits,
	marginMode,
}: Props) {
	const [shouldAutoOpenTooltip, setShouldAutoOpenTooltip] = useState(false);
	const [isAutoOpenTooltipOpen, setIsAutoOpenTooltipOpen] = useState(false);
	const [tooltipStatus, setTooltipStatus] = useSyncLocalStorage<
		'pending' | 'seen'
	>(ISOLATED_AVAILABLE_TOOLTIP_STORAGE_KEY, 'pending');

	const usdcSpotMarketInfo = useBalanceRowData({
		spotMarketIndex: QUOTE_SPOT_MARKET_INDEX,
	});

	const collateralAvailable = useMemo(() => {
		if (marginMode === 'cross') {
			return freeCollateral;
		}

		const usdcBalance = usdcSpotMarketInfo?.depositsQuote.toNum() ?? 0;
		return Math.min(freeCollateral, usdcBalance);
	}, [marginMode, usdcSpotMarketInfo, freeCollateral]);

	const { label, value } = (() => {
		if (reduceOnly) {
			return {
				label: 'Position',
				value: capStringFractionDigits(currentPositionBaseSizeString, {
					maxFractionDigits: sizeDigits,
				}),
			};
		}

		return {
			label: 'Available',
			value: Math.max(collateralAvailable, 0).toLocaleString(Env.locale, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}),
		};
	})();

	const valueContent = (
		<span>
			{`${reduceOnly ? '' : '$'}${value} ${
				reduceOnly ? formattedMarketSymbol : ''
			}`}
		</span>
	);

	const tradingToolTipDefinitionId =
		marginMode === 'cross' ? 'tradingCrossMargin' : 'tradingIsolatedMargin';
	const shouldCheckAutoOpen = marginMode === 'isolated' && !reduceOnly;

	useEffect(() => {
		if (!shouldCheckAutoOpen) {
			setShouldAutoOpenTooltip(false);
			setIsAutoOpenTooltipOpen(false);
			return;
		}

		if (tooltipStatus === 'pending') {
			setShouldAutoOpenTooltip(true);
			setIsAutoOpenTooltipOpen(true);
			setTooltipStatus('seen');
		}
	}, [shouldCheckAutoOpen, tooltipStatus, setTooltipStatus]);

	const handleAutoOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setIsAutoOpenTooltipOpen(false);
			setShouldAutoOpenTooltip(false);
			return;
		}
		setIsAutoOpenTooltipOpen(true);
	};

	// When auto-opening, use Popover (click-away to close)
	if (shouldAutoOpenTooltip) {
		const { title, body } =
			DEFINITION_TOOLTIP_CONFIG_BY_ID['tradingIsolatedMargin'];

		return (
			<span
				className="flex items-center gap-x-xs typo-body-xs text-text-default"
				data-testid="trade-form-max-size-label"
			>
				<Popover.Root
					open={isAutoOpenTooltipOpen}
					onOpenChange={handleAutoOpenChange}
				>
					<Popover.Trigger
						className={cm(
							TOOLTIP_TRIGGER_DECORATION_CLASSNAME,
							'text-text-secondary'
						)}
					>
						{label}:
					</Popover.Trigger>
					<Popover.Portal>
						<Popover.Content
							side="top"
							align="center"
							sideOffset={4}
							className={cm(
								TOOLTIP_CONTENT_CLASSNAME,
								'data-[state=open]:animate-fade-in'
							)}
						>
							<div className="flex flex-col gap-y-sm">
								<span className="typo-label-xs">{title}</span>
								<span className="flex flex-col gap-y-xs">{body}</span>
							</div>
							<Popover.Arrow
								width={16}
								height={8}
								className="fill-tooltip-bg"
							/>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
				{valueContent}
			</span>
		);
	}

	// Normal case: use DefinitionTooltip with hover behavior
	return (
		<span
			className="flex items-center gap-x-xs typo-body-2xs text-text-default md:typo-body-xs"
			data-testid="trade-form-max-size-label"
		>
			<DefinitionTooltip
				className="text-text-secondary"
				definitionId={reduceOnly ? undefined : tradingToolTipDefinitionId}
				disabled={reduceOnly}
			>
				{label}:
			</DefinitionTooltip>
			{valueContent}
		</span>
	);
});

export default MaxSizeLabel;
